import {
	Component,
	ElementRef,
	forwardRef,
	HostListener,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LuTooltipModalComponent, TooltipModalCnf } from 'lu-tooltip-modal';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { KEY_F1, LuSwitchConfigOptions } from './lu-switch.models';

export const defaultSwitchConfigOptions: LuSwitchConfigOptions = {
	disabled: false,
	truthyText: '',
	falsyText: '',
	tooltipMessage: ''
};

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'lu-switch',
	templateUrl: './lu-switch.component.html',
	styleUrls: ['./lu-switch.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LuSwitchComponent),
			multi: true
		}
	]
})
export class LuSwitchComponent implements OnInit, OnDestroy, ControlValueAccessor {
	@ViewChild('switchInfo') switchInfo: ElementRef;
	@Input() options: LuSwitchConfigOptions = defaultSwitchConfigOptions;
	value: boolean;
	ariaInfo: string;
	private tooltipModalRef: BsModalRef;
	private subscription: Subscription = new Subscription();

	constructor(
		@Inject(BsModalService) private readonly bsModalService: BsModalService,
		@Inject(TranslateService) private readonly translateService: TranslateService
	) {}

	/**
	 * On Component destroy
	 */
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	onChange: any = () => {};

	onTouch: any = () => {};

	ngOnInit(): void {
		this.options = {
			...defaultSwitchConfigOptions,
			...this.options
		};
		this.ariaInfo = this.getAriaInfo();
	}

	toggle(event: Event, state: boolean = false): void {
		if (event) {
			event.preventDefault();
		}
		const { disabled, falsyText, truthyText } = this.options;
		if (disabled) {
			return;
		}

		if (falsyText && truthyText && state !== null) {
			this.value = state;
		} else {
			this.value = !this.value;
		}
		this.onChange(this.value);
	}

	writeValue(newModel: boolean): void {
		this.value = newModel;
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouch = fn;
	}

	openTooltip(event: Event): void {
		const { tooltipMessage } = this.options;
		if (event) {
			event.preventDefault();
		}
		if (tooltipMessage) {
			const options: TooltipModalCnf = {
				message: tooltipMessage
			};
			this.tooltipModalRef = this.bsModalService.show(LuTooltipModalComponent, {
				initialState: {
					options
				}
			});
			this.afterModalClose();
		}
	}

	@HostListener('keydown', ['$event'])
	handleKeyupEvent(event: KeyboardEvent): void {
		const { disabled } = this.options;
		if (disabled) {
			return;
		}
		switch (event.key) {
			case KEY_F1:
				this.openTooltip(event);
				break;
		}
	}

	private getAriaInfo(): string {
		const { truthyText, falsyText, tooltipMessage } = this.options;
		return `${falsyText ?? ''} ${truthyText ?? ''}${tooltipMessage ? ', ' + this.translateService.instant('lup.more_info.help') : ''}`;
	}

	/**
	 * After tooltip modal closes we want to return focus to icon element
	 */
	private afterModalClose(): void {
		this.subscription.add(
			this.bsModalService.onHidden.pipe(take(1)).subscribe(() => {
				if (this.switchInfo?.nativeElement) {
					this.switchInfo.nativeElement.focus();
				}
			})
		);
	}
}
